<template>
  <card>
    <template #header>
      <h2 class="font-medium text-blue dark:text-white">
        Debt projection
      </h2>
    </template>

    <div class="grid md:grid-cols-2 rounded-lg">
      <div class="p-3 bg-gray-50 dark:bg-gray-800/50 md:rounded-bl-lg border-b md:border-b-0 md:border-r border-gray-300 dark:border-gray-800">
        <div class="space-y-3">
          <div
            v-if="showResidentToggle"
            class="flex gap-5"
          >
            <div class="flex gap-1 items-center">
              <input
                id="resident"
                v-model="resident"
                type="radio"
                value="1"
              >
              <label for="resident">Resident</label>
            </div>
            <div class="flex gap-1 items-center">
              <input
                id="non_resident"
                v-model="resident"
                type="radio"
                value="0"
              >
              <label for="non_resident">Non-resident</label>
            </div>
          </div>
          <div>
            <div class="flex justify-between">
              <div>
                <label class="inline mb-1">
                  Tuition and fees:
                </label>
                <span class="font-semibold text-blue dark:text-white">
                  {{ tuitionDisplay }}/y
                </span>
                <span v-if="discountDisplay" class="text-xs text-gray-500">
                  after {{ discountDisplay }} discount
                </span>
              </div>
              <div class="text-gray-600">
                {{ tuitionPercent }}%
              </div>
            </div>

            <l-range-input
              v-model="tuitionPercent"
              :min="0"
              :max="100"
              :step="20"
            />
          </div>
          <div>
            <div class="flex justify-between">
              <div>
                <label class="inline mb-1">
                  Cost of living:
                </label>
                <span class="font-semibold text-blue dark:text-white">
                  {{ colDisplay }}/y
                </span>
              </div>
              <div class="text-gray-600">
                {{ colPercent }}%
              </div>
            </div>

            <l-range-input
              v-model="colPercent"
              :min="0"
              :max="100"
              :step="20"
            />
          </div>

          <slot/>
        </div>
      </div>
      <div class="col-span-1 w-full">
        <debt-projection :debt="projectedDebt" :finances="finances" :median-earnings="medianEarnings"/>
      </div>
    </div>
  </card>
</template>

<script setup>

import Card from "@/components/cards/Card.vue"
import DebtProjection from "@/components/DebtProjection.vue"
import LRangeInput from "@/components/inputs/LRangeInput.vue"
import { computed, ref } from "vue"
import { currency, currencyShort } from "@/support"

const props = defineProps({
  school: { type: Object, required: true },
  debt: { type: Object, required: true },
  finances: { type: Object },
  medianEarnings: { type: Number },
})
const showResidentToggle = computed(() => props.school.offers_discount)
const resident = ref(showResidentToggle.value ? 1 : 0)

const startingTuition = computed(() =>
  (resident.value > 0 ? props.school.latest_prices.resident_tuition_FT : props.school.latest_prices.tuition_FT),
)
const tuitionPercent = ref(100)
const tuition = computed(() => Math.round(startingTuition.value * (tuitionPercent.value / 100)))
const tuitionDisplay = computed(() => currency(tuition.value))
const discountDisplay = computed(() => (tuition.value < startingTuition.value ? currencyShort(startingTuition.value - tuition.value) : null))

const startingCol = computed(() => props.school.latest_prices.col)
const colPercent = ref(100)
const col = computed(() => Math.round(startingCol.value * (colPercent.value / 100)))
const colDisplay = computed(() => currency(col.value))

const projectedDebt = computed(() => props.debt[resident.value > 0 ? "debt_resident" : "debt"][`tuition_${100 - tuitionPercent.value}_col_${colPercent.value}`])
</script>
